import React from 'react';
import * as Sentry from '@sentry/react';
import {
  scrubSentryEvent,
  scrubSentryBreadcrumb,
} from '@shared/utils/sentryScrub';

function normalizeEnv(value) {
  if (
    value == null ||
    value === '' ||
    value === 'undefined' ||
    value === 'null'
  ) {
    return '';
  }
  return String(value);
}

const dsn = normalizeEnv(process.env.REACT_APP_SENTRY_DSN);
const appEnv =
  normalizeEnv(process.env.APP_ENV) ||
  normalizeEnv(process.env.NODE_ENV) ||
  'development';

const tracesSampleRate = Number(
  normalizeEnv(process.env.SENTRY_TRACES_SAMPLE_RATE) ||
    (appEnv === 'production' ? '0.2' : '1')
);

const releaseRaw = normalizeEnv(process.env.SENTRY_RELEASE);

if (
  typeof window !== 'undefined' &&
  dsn &&
  !window.__SENTRY_BROWSER_INITIALIZED__
) {
  Sentry.init({
    dsn,
    environment: appEnv,
    release: releaseRaw || undefined,
    // 100% of error events (traces still follow tracesSampleRate).
    sampleRate: 1,
    // The page URL can be a login link (/autologin/<code>, /reset/...), so
    // clean it from events, transactions and breadcrumbs before sending.
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
    // Dedupe is on by default; passing `integrations` as an array *merges* with defaults and
    // leaves Dedupe installed. Use the callback form so defaults are replaced without Dedupe.
    integrations: (defaults) => [
      ...defaults.filter((integration) => integration.name !== 'Dedupe'),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 1,
  });
  window.__SENTRY_BROWSER_INITIALIZED__ = true;
}

/**
 * Wrap the root tree so React render errors are reported (they do not reach window.onerror).
 * Import from this module so Sentry.init runs before render.
 */
export function SentryAppRoot({ children }) {
  if (!dsn) {
    return children;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
          <p>Something went wrong.</p>
          <button type="button" onClick={resetError}>
            Try again
          </button>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default Sentry;
